import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useReactFlow } from "reactflow";
import {
  useDeleteAllPackaging as useDeleteFarmGatePackaging,
  useGetPackagingData as useGetFarmGatePackagingData,
  useUpdatePackagingDetails as useUpdateFarmGatePackagingDetails
} from "../../../../../../api/farmGate/packaging";
import {
  useDeleteAllPackaging as useDeleteProcessGatePackaging,
  useGetPackagingData as useGetProcessGatePackagingData,
  useUpdatePackagingDetails as useUpdateProcessGatePackagingDetails
} from "../../../../../../api/processGate/packaging";
import { useGetPackagingMaterial } from "../../../../../../api/static-data";
import { DrawingContext } from "../../../../../../components/drawing-tool/DrawingTool";
import { errorView, successMessage } from "../../../../../../helpers/ErrorHandler";
import { removeNewDrawingItemStatus, saveNewDrawingItem } from "../../../../../../utils/cacheStorage";
import { PACKAGING_LEVELS_CONFIG } from "../packagingBaseLevels.config";
import {
  PackagingDrawingNode,
  PackagingModalData,
  SaveDocumentDataHandle
} from "../packagingBase.types";
import { PACKAGING_GATE, PackagingGate } from "../packagingLevel.types";
import {
  buildPackagingDetailsPayload,
  PackagingUnitSaveValue
} from "../utils/buildPackagingDetailsPayload";
import { validatePackagingName } from "../utils/validatePackagingName";
import {
  useExistingPackagingName,
  usePackagingIdRefetch,
  usePackagingNameForm,
  usePackagingUnitState,
  useUnitSizeWeightUnitSync
} from "./usePackagingBase";

const GATE_UI = {
  "farm-gate": {
    gateIdKey: "farmGateId" as const,
    gateIdProp: "farmGateId",
    deleteTitle: "Delete Farm gate packaging",
    deleteSubtitle:
      "Are you sure you want to remove the Farm gate packaging? Please consider you cannot recover these data after remove",
    notesDisabledWhenNoName: true,
    notesAlwaysShowSaveButton: true
  },
  "process-gate": {
    gateIdKey: "processGateId" as const,
    gateIdProp: "processGateId",
    deleteTitle: "Delete Process gate packaging",
    deleteSubtitle:
      "Are you sure you want to remove the Process gate packaging? Please consider you cannot recover these data after remove",
    notesDisabledWhenNoName: false,
    notesAlwaysShowSaveButton: false
  }
};

interface UsePackagingBaseScreenOptions {
  gate: PackagingGate;
  modalData: PackagingModalData;
}

export const usePackagingBaseScreen = ({ gate, modalData }: UsePackagingBaseScreenOptions) => {
  const { nodeItem } = modalData;
  const gateId = gate === "farm-gate" ? modalData.farmGateId! : modalData.processGateId!;
  const gateUi = GATE_UI[gate];
  const isFarmGate = gate === PACKAGING_GATE.FARM;

  const {
    nodeItemId,
    chartEdges,
    chartNodes,
    setNodeItem,
    setChartNodes,
    lockStatus,
    deleteLockStatus: rawDeleteLockStatus,
    saveDrawing
  } = useContext(DrawingContext);
  const deleteLockStatus = rawDeleteLockStatus ?? lockStatus;

  const [packagingId, setPackagingId] = useState<string | null>(
    nodeItem?.data?.reference?.packagingId ?? null
  );
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);

  const notesRef = useRef<SaveDocumentDataHandle>(null);
  const { deleteElements, getEdges } = useReactFlow();

  const farmPackagingQuery = useGetFarmGatePackagingData(
    isFarmGate ? packagingId ?? undefined : undefined
  );
  const processPackagingQuery = useGetProcessGatePackagingData(
    isFarmGate ? undefined : packagingId ?? undefined
  );
  const packagingQuery = isFarmGate ? farmPackagingQuery : processPackagingQuery;
  const {
    data,
    status,
    isInitialLoading: isPackagingDataInitialLoading,
    isFetching: isPackagingDataFetching,
    refetch
  } = packagingQuery;

  const {
    data: packagingTypeData,
    isInitialLoading: isPackagingMaterialInitialLoading,
    isFetching: isPackagingMaterialFetching
  } = useGetPackagingMaterial();

  const farmUpdateDetails = useUpdateFarmGatePackagingDetails();
  const processUpdateDetails = useUpdateProcessGatePackagingDetails();
  const updatePackagingDetails = isFarmGate ? farmUpdateDetails : processUpdateDetails;

  const farmDeletePackaging = useDeleteFarmGatePackaging();
  const processDeletePackaging = useDeleteProcessGatePackaging();
  const deleteAllPackaging = isFarmGate ? farmDeletePackaging : processDeletePackaging;

  const {
    form,
    hasSavedName,
    nameExpanded,
    setNameExpanded,
    isNameLocked,
    startNameEdit,
    finishNameSave
  } = usePackagingNameForm(data?.packagingName);

  const {
    isSaveBtnClicked,
    setIsSaveBtnClicked,
    clickedPackagingUnit,
    setClickedPackagingUnit,
    unitSizeWeightUnit,
    setUnitSizeWeightUnit
  } = usePackagingUnitState();

  useUnitSizeWeightUnitSync(data?.unitSizeWeightUnit, setUnitSizeWeightUnit);
  usePackagingIdRefetch(packagingId, refetch);

  const existingPackagingNames = useExistingPackagingName(status, data?.packagingName);
  const typedChartNodes = chartNodes as PackagingDrawingNode[];

  const connectedEdges = getEdges().filter(
    edge => edge.source === nodeItemId || edge.target === nodeItemId
  );

  const handleComponentDataSave = useCallback(
    async (id: string) => {
      try {
        await notesRef.current?.saveDocumentAndNote(id);
      } catch (error) {
        errorView(error);
      }
    },
    []
  );

  const savePackingNodeData = useCallback(
    async (id: string, packagingName?: string) => {
      try {
        const updatedNode: PackagingDrawingNode = {
          ...nodeItem,
          data: {
            ...nodeItem?.data,
            ...(packagingName && { label: `Packaging Details (${packagingName})` }),
            reference: { ...nodeItem?.data?.reference, packagingId: id },
            description: "Packaging Details"
          }
        };

        await notesRef.current?.saveDocumentAndNote(id);

        const updatedChartNodes = typedChartNodes.map(n =>
          n.id === nodeItem?.id ? updatedNode : n
        );

        setChartNodes(updatedChartNodes as never[]);
        setNodeItem(updatedNode as never);
        saveDrawing(chartEdges, updatedChartNodes);
      } catch (error) {
        errorView(error);
      }
    },
    [chartEdges, nodeItem, saveDrawing, setChartNodes, setNodeItem, typedChartNodes]
  );

  const removePackagingNode = useCallback(() => {
    notesRef.current?.deleteComment();
    deleteElements({ nodes: [{ id: nodeItem.id }], edges: connectedEdges });
    setNodeItem(null);
  }, [connectedEdges, deleteElements, nodeItem.id, setNodeItem]);

  const { clearErrors, setError, setFocus } = form;

  const updatePackagingName = useCallback(
    (formData: { packagingName: string }) => {
      const validation = validatePackagingName({
        packagingName: formData.packagingName ?? "",
        existingPackagingName: existingPackagingNames,
        chartNodes: typedChartNodes
      });

      if (!validation.isValid) {
        clearErrors("packagingName");
        setFocus("packagingName");
        setError("packagingName", { type: "manual", message: validation.errorMessage });
        return;
      }

      updatePackagingDetails.mutate(
        {
          data: { packagingName: formData.packagingName },
          [gateUi.gateIdKey]: gateId,
          packagingId
        },
        {
          onSuccess: (response: { _id: string; packagingName: string }) => {
            setPackagingId(response._id);

            if (packagingId) {
              if (isFarmGate) removeNewDrawingItemStatus();
              refetch();
            } else if (isFarmGate) {
              saveNewDrawingItem("created");
            }

            successMessage("Packaging name updated successfully");
            savePackingNodeData(response._id, response.packagingName);
            finishNameSave();
          },
          onError: error => errorView(error)
        }
      );
    },
    [
      clearErrors,
      existingPackagingNames,
      finishNameSave,
      gateId,
      gateUi.gateIdKey,
      isFarmGate,
      packagingId,
      refetch,
      savePackingNodeData,
      setError,
      setFocus,
      typedChartNodes,
      updatePackagingDetails
    ]
  );

  const updatePackingDetails = useCallback(
    (value: PackagingUnitSaveValue, type: string) => {
      if (!packagingId) {
        errorView("Please save the packaging name before saving unit details.");
        return;
      }

      const payload = buildPackagingDetailsPayload({
        gateId,
        gateIdKey: gateUi.gateIdKey,
        packagingId,
        packagingType: type,
        unitSizeWeightUnit,
        value,
        snapshot: data
      });

      updatePackagingDetails.mutate(payload, {
        onSuccess: (response: { _id: string }) => {
          handleComponentDataSave(packagingId);
          if (packagingId) {
            refetch();
            return;
          }
          savePackingNodeData(response._id);
        },
        onError: error => errorView(error)
      });
    },
    [
      data,
      gateId,
      gateUi.gateIdKey,
      handleComponentDataSave,
      packagingId,
      refetch,
      savePackingNodeData,
      unitSizeWeightUnit,
      updatePackagingDetails
    ]
  );

  const handlePackagingDelete = useCallback(() => {
    const dataToDelete = { packagingId, [gateUi.gateIdKey]: gateId };

    if (packagingId) {
      deleteAllPackaging.mutate(dataToDelete, {
        onSuccess: () => {
          removePackagingNode();
          if (!isFarmGate) successMessage("Packaging deleted successfully");
          setVisibleDeleteModal(false);
        },
        onError: () => setVisibleDeleteModal(false)
      });
      return;
    }

    removePackagingNode();
    setVisibleDeleteModal(false);
  }, [deleteAllPackaging, gateId, gateUi.gateIdKey, isFarmGate, packagingId, removePackagingNode]);

  const isPageLoading =
    isPackagingDataInitialLoading ||
    isPackagingMaterialInitialLoading ||
    deleteAllPackaging.isLoading;

  const isPageFetching =
    (isPackagingDataFetching || isPackagingMaterialFetching) && !isPageLoading;

  const getPackageData = useCallback(
    (param: string) => (data?.[param] as unknown[]) || [],
    [data]
  );

  const sharedPackagingProps = useMemo(
    () => ({
      packagingId,
      packagingName: data?.packagingName,
      refetchData: refetch,
      [gateUi.gateIdProp]: gateId,
      savePackingNodeData,
      packagingTypeData,
      setIsSaveBtnClicked,
      isSaveBtnClicked,
      setClickedPackagingUnit,
      clickedPackagingUnit,
      onPackagingSave: handleComponentDataSave,
      lockStatus
    }),
    [
      packagingId,
      data?.packagingName,
      refetch,
      gateUi.gateIdProp,
      gateId,
      savePackingNodeData,
      packagingTypeData,
      setIsSaveBtnClicked,
      isSaveBtnClicked,
      setClickedPackagingUnit,
      clickedPackagingUnit,
      handleComponentDataSave,
      lockStatus
    ]
  );

  const levelSections = useMemo(
    () =>
      PACKAGING_LEVELS_CONFIG.map(level => ({
        key: level.packagingType,
        props: {
          ...sharedPackagingProps,
          packagingType: level.packagingType,
          unitSizes: data?.[level.unitSizesKey] as number,
          totalWeightOfItem: data?.[level.totalWeightKey] as number,
          updatePackingDetails,
          changeUnitsType: level.changeUnitsType,
          packagingData: getPackageData(level.dataKey),
          ...(level.includeWeightUnit
            ? { weightUnit: data?.unitSizeWeightUnit, unitSizeWeightUnit, setUnitSizeWeightUnit }
            : {}),
          ...(level.includePallet
            ? { palletWidth: data?.palletWidth, palletLength: data?.palletLength }
            : {})
        }
      })),
    [data, getPackageData, sharedPackagingProps, unitSizeWeightUnit, updatePackingDetails, setUnitSizeWeightUnit]
  );

  const machineryProps = useMemo(
    () => ({
      packagingId,
      packagingName: data?.packagingName,
      refetchData: refetch,
      [gateUi.gateIdProp]: gateId,
      packagingData: getPackageData("packagingMethod"),
      savePackingNodeData,
      onPackagingSave: handleComponentDataSave,
      lockStatus,
      gate
    }),
    [
      packagingId,
      data?.packagingName,
      refetch,
      gateUi.gateIdProp,
      gateId,
      getPackageData,
      savePackingNodeData,
      handleComponentDataSave,
      lockStatus,
      gate
    ]
  );

  const notesProps = useMemo(
    () => ({
      ref: notesRef,
      parentId: packagingId,
      isDisabled:
        !!lockStatus || (gateUi.notesDisabledWhenNoName ? !data?.packagingName : false),
      showSaveButton: gateUi.notesAlwaysShowSaveButton ? true : !!data?.packagingName
    }),
    [
      packagingId,
      data?.packagingName,
      lockStatus,
      gateUi.notesDisabledWhenNoName,
      gateUi.notesAlwaysShowSaveButton
    ]
  );

  return {
    isPageLoading,
    isPageFetching,
    nameSectionProps: {
      packagingName: data?.packagingName || "",
      nameExpanded,
      onToggleExpanded: () => setNameExpanded(prev => !prev),
      onEditName: startNameEdit,
      onDelete: () => setVisibleDeleteModal(true),
      form,
      onSaveName: updatePackagingName,
      lockStatus,
      deleteLockStatus,
      isNameLocked
    },
    hasSavedName,
    levelSections,
    machineryProps,
    notesProps,
    deleteModalProps: {
      visible: visibleDeleteModal,
      onClose: () => setVisibleDeleteModal(false),
      onConfirm: handlePackagingDelete,
      title: gateUi.deleteTitle,
      subtitle: gateUi.deleteSubtitle
    },
    onOpenDeleteModal: () => setVisibleDeleteModal(true),
    lockStatus
  };
};
